import type { QueryClient } from "@tanstack/react-query";

import type { GitpodAPI } from "@/api";
import { handleNotificationEvent } from "@/hooks/use-notifications";
import { handleAgentExecutionEvent } from "@/queries/agent-queries";
import { handleWorkflowEvents } from "@/queries/automation-queries";
import { handleEnvironmentClassEvent, handleEnvironmentEvent } from "@/queries/environment-queries";
import { handleGroupMembershipEvent, handleRoleAssignmentEvent } from "@/queries/group-queries";
import { handleOrganizationPolicyEvent } from "@/queries/organization-queries";
import { handlePrebuildEvent } from "@/queries/prebuild-queries";
import { handleProjectEvent } from "@/queries/project-queries";
import { handlePromptEvent } from "@/queries/prompt-queries";
import { handleHostAuthenticationTokenEvent } from "@/queries/runner-configuration-queries";
import { handleRunnerEvent } from "@/queries/runner-queries";
import { handleProjectSecretEvent, handleUserSecretEvent } from "@/queries/secret-queries";
import { handleServiceAccountEvent } from "@/queries/service-account-queries";
import { handleUserPreferenceEvent } from "@/queries/user-preferences-queries";
import { handleWarmPoolEvent } from "@/queries/warm-pool-queries";
import type { WatchEventsResponse } from "gitpod-next-api/gitpod/v1/event_pb";
import { ResourceOperation, ResourceType } from "gitpod-next-api/gitpod/v1/event_pb";

export const DEBOUNCE_DELAY_ORG_MS = 500;
export const MAX_WAIT_MS = 2000;

/**
 * Creates an event dispatcher for org-scoped events. Handles debouncing of
 * environment UPDATE events and dispatches to the appropriate query handler.
 *
 * Returns a dispatch function and a cleanup function to clear pending timers.
 *
 * NOTE: This is the scoped path, used when the `scoped_event_streams_enabled`
 * flag is ON (branching happens in `OrgEventStreamProvider.tsx`). The legacy
 * path lives in `use-stream-events.ts`. When adding a new ResourceType handler
 * here, also add it there (and vice versa) until the legacy path is removed.
 */
export function createOrgEventDispatcher(organizationId: string) {
    const pendingDebounceTimers = new Map<string, ReturnType<typeof setTimeout>>();
    const burstStartTimes = new Map<string, number>();

    const dispatch = async (api: typeof GitpodAPI, client: QueryClient, response: WatchEventsResponse) => {
        switch (response.resourceType) {
            case ResourceType.ENVIRONMENT:
                if (response.operation === ResourceOperation.UPDATE) {
                    const envId = response.resourceId;
                    const now = Date.now();
                    const existing = pendingDebounceTimers.get(envId);
                    if (existing !== undefined) {
                        clearTimeout(existing);
                    }
                    if (!burstStartTimes.has(envId)) {
                        burstStartTimes.set(envId, now);
                    }
                    const elapsed = now - burstStartTimes.get(envId)!;
                    const delay = Math.max(0, Math.min(DEBOUNCE_DELAY_ORG_MS, MAX_WAIT_MS - elapsed));
                    pendingDebounceTimers.set(
                        envId,
                        setTimeout(() => {
                            pendingDebounceTimers.delete(envId);
                            burstStartTimes.delete(envId);
                            handleEnvironmentEvent(api, client, response, { skipCacheCheck: true }).catch((err) =>
                                console.error(
                                    `[OrgEventDispatch] Failed to handle debounced environment event for ${envId}:`,
                                    err,
                                ),
                            );
                        }, delay),
                    );
                } else {
                    await handleEnvironmentEvent(api, client, response, { skipCacheCheck: true });
                }
                break;
            case ResourceType.RUNNER:
                await handleRunnerEvent(api, client, response);
                break;
            case ResourceType.ENVIRONMENT_CLASS:
                await handleEnvironmentClassEvent(client);
                break;
            case ResourceType.PROJECT:
                await handleProjectEvent(api, client, response);
                break;
            case ResourceType.PREBUILD:
                await handlePrebuildEvent(api, client, response);
                break;
            case ResourceType.USER_PREFERENCE:
                await handleUserPreferenceEvent(api, client, response);
                break;
            case ResourceType.SECRET:
                await handleProjectSecretEvent(api, client, response);
                break;
            case ResourceType.USER_SECRET:
                await handleUserSecretEvent(api, client, response);
                break;
            case ResourceType.AGENT_EXECUTION:
                await handleAgentExecutionEvent(api, client, response);
                break;
            case ResourceType.ORGANIZATION_POLICY:
                await handleOrganizationPolicyEvent(api, client, response, organizationId);
                break;
            case ResourceType.PROMPT:
                handlePromptEvent(api, client, response);
                break;
            case ResourceType.WORKFLOW:
            case ResourceType.WORKFLOW_EXECUTION:
            case ResourceType.WORKFLOW_EXECUTION_ACTION:
                await handleWorkflowEvents(api, client, response);
                break;
            case ResourceType.ROLE_ASSIGNMENT_CHANGED:
                await handleRoleAssignmentEvent(client);
                break;
            case ResourceType.GROUP_MEMBERSHIP_CHANGED:
                await handleGroupMembershipEvent(client);
                break;
            case ResourceType.SERVICE_ACCOUNT:
                await handleServiceAccountEvent(api, client, response);
                break;
            case ResourceType.HOST_AUTHENTICATION_TOKEN:
                await handleHostAuthenticationTokenEvent(api, client, response);
                break;
            case ResourceType.WARM_POOL:
                await handleWarmPoolEvent(client, response);
                break;
            case ResourceType.NOTIFICATION:
                await handleNotificationEvent(api, client, response);
                break;
            default:
                console.warn(`[OrgEventDispatch] Unhandled resource type:`, response.resourceType);
        }
    };

    const cleanup = () => {
        for (const timer of pendingDebounceTimers.values()) {
            clearTimeout(timer);
        }
        pendingDebounceTimers.clear();
        burstStartTimes.clear();
    };

    return { dispatch, cleanup };
}
